import { useEffect, useState } from 'react'
import {
  Calendar,
  Check,
  Download,
  MapPin,
  Printer,
  QrCode,
  RefreshCw,
  Send,
  Tag,
  User,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ticketingService } from '../services/ticketing.service'
import { TicketTransferDialog } from './TicketTransferDialog'
import type { CurrentOwnerTicket, TicketTransferView } from '../types'
import { formatUserFacingError, getErrorCode, withErrorCode } from '@/lib/errors'

interface TicketCardProps {
  ticket: CurrentOwnerTicket
  pdfUrl?: string | null
  ownerAccessToken?: string
  onRefresh?: () => Promise<void>
}

function asDate(value: unknown): Date | null {
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  if (value && typeof value === 'object' && 'toDate' in value) {
    const candidate = value as { toDate?: () => Date }
    const date = candidate.toDate?.()
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null
  }
  return null
}

function formatEventDate(value: unknown): string {
  const date = asDate(value)
  if (!date) return withErrorCode('Event date unavailable.', 'ticketing/event-date-unavailable')
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function isSafeImageUrl(value: string | null | undefined): value is string {
  if (!value) return false
  if (/^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(value)) return true
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

function errorMessage(error: unknown, fallback: string): string {
  const errorCode = getErrorCode(error, 'ticketing/action-failed')
  const code = errorCode.replace(/^functions\//, '')
  if (code === 'failed-precondition') {
    return withErrorCode(
      'This ticket changed or is no longer available for that action. Refresh and try again.',
      errorCode,
    )
  }
  if (code === 'permission-denied' || code === 'unauthenticated') {
    return withErrorCode(
      'This secure ticket link is no longer authorized. Request a fresh ticket link or sign in.',
      errorCode,
    )
  }
  return formatUserFacingError(error, fallback, errorCode)
}

function statusLabel(ticket: CurrentOwnerTicket): string {
  if (ticket.checkedIn || ticket.status === 'used') return 'Checked in'
  if (ticket.status === 'refunded') return 'Refunded'
  if (ticket.status === 'valid') return 'Valid'
  return 'Unavailable'
}

function statusVariant(ticket: CurrentOwnerTicket): 'default' | 'secondary' | 'destructive' {
  if (ticket.status === 'valid' && !ticket.checkedIn) return 'default'
  if (ticket.checkedIn || ticket.status === 'used') return 'secondary'
  return 'destructive'
}

export function TicketCard({
  ticket,
  pdfUrl,
  ownerAccessToken,
  onRefresh,
}: TicketCardProps) {
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [transferSubmitting, setTransferSubmitting] = useState(false)
  const [transferError, setTransferError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingTransfer, setPendingTransfer] = useState<TicketTransferView | null | undefined>(undefined)

  useEffect(() => {
    // A refresh is authoritative. Clear optimistic transfer state whenever
    // the server's pending-transfer identity changes (including claim/cancel
    // in another browser).
    setPendingTransfer(undefined)
  }, [ticket.id, ticket.pendingTransferId, ticket.pendingTransfer?.expiresAt])

  const effectivePendingTransfer = pendingTransfer === undefined
    ? ticket.pendingTransfer ?? null
    : pendingTransfer
  const pendingTransferId = effectivePendingTransfer?.transferId ?? ticket.pendingTransferId ?? null
  const isValid = ticket.status === 'valid' && ticket.checkedIn !== true
  const canTransfer = isValid && ticket.canTransfer !== false && !pendingTransferId
  const currentOwnerEmail =
    ticket.currentOwnerEmail ?? ticket.currentOwnerEmailNormalized ?? ticket.purchaserEmail
  const qrImageUrl = ticket.qrCodeUrl ?? ticket.qrImageUrl

  async function handleTransfer(recipientEmail: string) {
    setTransferSubmitting(true)
    setTransferError(null)
    setActionError(null)
    try {
      const result = await ticketingService.createTicketTransfer({
        ticketId: ticket.id,
        recipientEmail,
        ownerAccessToken,
      })
      setPendingTransfer({ ...result, recipientEmail })
      setTransferDialogOpen(false)
      if (!result.emailSent) {
        setActionError(
          withErrorCode(
            'The transfer was saved, but the email could not be delivered. Use “Resend link” to try again.',
            'ticketing/transfer-email',
          ),
        )
      }
      await onRefresh?.()
    } catch (error) {
      setTransferError(errorMessage(error, 'We could not send the transfer link. Please try again.'))
    } finally {
      setTransferSubmitting(false)
    }
  }

  async function handleCancelTransfer() {
    if (!pendingTransferId || !window.confirm('Cancel this unclaimed ticket transfer?')) return
    setActionError(null)
    try {
      await ticketingService.cancelTicketTransfer({
        transferId: pendingTransferId,
        ownerAccessToken,
      })
      setPendingTransfer(null)
      await onRefresh?.()
    } catch (error) {
      setActionError(errorMessage(error, 'We could not cancel the transfer. Please refresh and try again.'))
    }
  }

  async function handleResendTransfer() {
    if (!pendingTransferId) return
    setActionError(null)
    try {
      const result = await ticketingService.resendTicketTransfer({
        transferId: pendingTransferId,
        ownerAccessToken,
      })
      setPendingTransfer((current) => ({
        ...result,
        recipientEmail: current?.recipientEmail,
      }))
      setActionError(
        result.emailSent
          ? null
          : withErrorCode(
              'The transfer is still pending, but the email could not be delivered. Please try resending again.',
              'ticketing/transfer-resend-email',
            ),
      )
      await onRefresh?.()
    } catch (error) {
      setActionError(errorMessage(error, 'We could not resend the transfer link. Please try again.'))
    }
  }

  return (
    <article className="overflow-hidden rounded-2xl border bg-card shadow-sm print:break-inside-avoid">
      <div className="border-b bg-muted/30 px-5 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold">{ticket.eventTitle}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{ticket.ticketTypeName}</p>
          </div>
          <Badge variant={statusVariant(ticket)}>{statusLabel(ticket)}</Badge>
        </div>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_15rem]">
        <div className="space-y-5">
          <div className="space-y-2.5 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formatEventDate(ticket.eventDate)}</span>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{ticket.eventVenue || 'Venue details coming soon'}</span>
            </div>
            <div className="flex items-start gap-2">
              <User className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <span className="font-medium text-foreground">Current owner</span>
                <br />
                {currentOwnerEmail || withErrorCode('Owner email unavailable.', 'ticketing/owner-email-unavailable')}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Tag className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Ticket reference <span className="font-mono text-xs">{ticket.id.slice(0, 10).toUpperCase()}</span>
              </span>
            </div>
          </div>

          {ticket.checkedIn && (
            <div className="flex items-start gap-2 rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-300">
              <Check className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {withErrorCode(
                  'This ticket has already been checked in and cannot be transferred.',
                  'ticketing/already-checked-in',
                )}
              </span>
            </div>
          )}

          {pendingTransferId && isValid && (
            <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
              <div>
                <p className="font-medium">Transfer pending</p>
                <p className="mt-1 text-muted-foreground">
                  Ownership stays with you until the recipient claims the secure link.
                  {effectivePendingTransfer?.recipientEmail
                    ? ` Sent to ${effectivePendingTransfer.recipientEmail}.`
                    : ''}
                </p>
                {effectivePendingTransfer?.expiresAt && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Link expires {formatEventDate(effectivePendingTransfer.expiresAt)}.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 print:hidden">
                <Button type="button" variant="outline" size="sm" onClick={() => void handleResendTransfer()}>
                  <RefreshCw className="h-4 w-4" />
                  Resend link
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => void handleCancelTransfer()}>
                  <X className="h-4 w-4" />
                  Cancel transfer
                </Button>
              </div>
            </div>
          )}

          {actionError && <p className="text-sm text-destructive">{actionError}</p>}

          <div className="flex flex-wrap gap-2 print:hidden">
            {canTransfer && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setTransferError(null)
                  setTransferDialogOpen(true)
                }}
              >
                <Send className="h-4 w-4" />
                Transfer ticket
              </Button>
            )}
            {pdfUrl && (
              <Button asChild type="button" variant="outline">
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
                  <Download className="h-4 w-4" />
                  Download / print PDF
                </a>
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => window.print()}>
              <Printer className="h-4 w-4" />
              Print page
            </Button>
          </div>

        </div>

        <div className="flex min-h-52 flex-col items-center justify-center rounded-xl border border-dashed bg-background p-4 text-center">
          {isSafeImageUrl(qrImageUrl) ? (
            <img
              src={qrImageUrl}
              alt={`QR code for ${ticket.eventTitle}`}
              className="h-48 w-48 rounded-lg object-contain"
            />
          ) : (
            <>
              <QrCode className="h-16 w-16 text-muted-foreground/50" aria-hidden="true" />
              <p className="mt-3 text-sm font-medium">Your QR ticket</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                The secure QR presentation is included in the printable ticket when a PDF is
                available.
              </p>
            </>
          )}
          <p className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground">
            Present at the door
          </p>
        </div>
      </div>

      <TicketTransferDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        onSubmit={handleTransfer}
        submitting={transferSubmitting}
        error={transferError}
      />
    </article>
  )
}
